/**
 * 문서 본문(편집 가능한 텍스트) ↔ 구조 (DOCUMENT_OUTPUTS §6).
 * 문서는 상담자가 자유롭게 고칠 수 있는 텍스트로 저장하고, 양식 화면·인쇄·DOCX는 이 텍스트를 해석해 만든다.
 *   # 제목 / ## 큰 항목 / ### 칸 이름 / #### 세부 칸 / "이름: 값 · 이름: 값" 머리 줄 / 축어록 줄 "상3: 발언"
 *   칸 안의 "| 가 | 나 |" 줄은 표로 본다.
 * 해석하지 못한 줄은 버리지 않고 일반 문단으로 둔다 (상담자가 쓴 내용 유실 없음).
 */
export type DocBlock =
  | { kind: 'section'; text: string }
  | { kind: 'field'; label: string; lines: string[]; sub?: boolean }
  | { kind: 'para'; text: string };

export type ParsedDoc = { title?: string; meta: { label: string; value: string }[]; blocks: DocBlock[] };

/** speaker는 표기와 무관한 기준값(상=상담자, 내=내담자), label은 문서에 적힌 그대로 */
export type VerbatimLine = {
  speaker: '상' | '내' | '?';
  label: string;
  no?: number;
  /** 번호를 문서에 적힌 그대로 (예: '₁₂') */
  noText?: string;
  uncertain: boolean;
  text: string;
};

/** 축어록 표기. 문서에는 항상 상/내로 저장하고, 보기·복사·인쇄·Word에서만 고른 표기로 바꾼다 */
export const VERBATIM_NOTATIONS = {
  ko: { counselor: '상', client: '내', legend: '상: 상담자, 내: 내담자', name: '상1 / 내1' },
  tc: { counselor: 'T', client: 'C', legend: 'T: 상담자(Therapist), C: 내담자(Client)', name: 'T1 / C1' },
  cocl: { counselor: 'Co', client: 'Cl', legend: 'Co: 상담자(Counselor), Cl: 내담자(Client)', name: 'Co1 / Cl1' },
  pc: { counselor: 'P', client: 'C', legend: 'P: 목회상담자, C: 내담자', name: 'P1 / C1 (목회상담)' },
  /** 목회상담 대화록 작성표: P₁, C₁ (아래첨자 번호) */
  pcsub: { counselor: 'P', client: 'C', legend: 'P: 목회상담자, C: 내담자', name: 'P₁ / C₁ (대화록 작성표)' },
} as const;

const SUB = '₀₁₂₃₄₅₆₇₈₉';
/** 12 → ₁₂ */
export const toSubscript = (digits: string) => digits.replace(/\d/g, (d) => SUB[Number(d)]!);
const fromSubscript = (s: string) => s.replace(/[₀-₉]/g, (d) => String(SUB.indexOf(d)));

/** 축어록(발언 번호 줄)이 들어 있는 칸: 사례보고서 '축어록 …', 대화록 '대화 내용 …' */
export const isVerbatimLabel = (label: string) => label.startsWith('축어록') || label.startsWith('대화 내용');
export type VerbatimNotation = keyof typeof VERBATIM_NOTATIONS;
export const isVerbatimNotation = (x: unknown): x is VerbatimNotation =>
  typeof x === 'string' && x in VERBATIM_NOTATIONS;

const META_LINE = /^[^:#\-\s][^:]{0,12}:\s.*(\s·\s[^:]{1,12}:\s.*)+$/;

export function parseMetaLine(line: string): { label: string; value: string }[] | null {
  if (!META_LINE.test(line)) return null;
  const parts = line.split(/\s·\s/).map((p) => {
    const i = p.indexOf(':');
    return i > 0 ? { label: p.slice(0, i).trim(), value: p.slice(i + 1).trim() } : null;
  });
  return parts.every(Boolean) ? (parts as { label: string; value: string }[]) : null;
}

export function parseDoc(content: string): ParsedDoc {
  const out: ParsedDoc = { meta: [], blocks: [] };
  let field: Extract<DocBlock, { kind: 'field' }> | null = null;
  for (const raw of content.replace(/\r/g, '').split('\n')) {
    const line = raw.trimEnd();
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1]!.length;
      field = null;
      if (level === 1 && !out.title) out.title = h[2]!;
      else if (level >= 3) {
        field = { kind: 'field', label: h[2]!, lines: [], ...(level === 4 ? { sub: true } : {}) };
        out.blocks.push(field);
      } else out.blocks.push({ kind: 'section', text: h[2]! });
      continue;
    }
    if (!out.blocks.length && !field) {
      const meta = parseMetaLine(line);
      if (meta) {
        out.meta.push(...meta);
        continue;
      }
    }
    if (field) {
      if (line.trim() || field.lines.length) field.lines.push(line);
    } else if (line.trim()) out.blocks.push({ kind: 'para', text: line });
  }
  for (const b of out.blocks) if (b.kind === 'field') while (b.lines.length && !b.lines.at(-1)!.trim()) b.lines.pop();
  return out;
}

const VB = /^(상|내|Co|Cl|T|C|P|\?)([0-9₀-₉]+)?(\(확인 필요\))?\s*:\s?(.*)$/;
const CANON: Record<string, VerbatimLine['speaker']> = {
  상: '상',
  T: '상',
  Co: '상',
  P: '상',
  내: '내',
  C: '내',
  Cl: '내',
  '?': '?',
};

export function parseVerbatimLine(line: string): VerbatimLine | null {
  const m = VB.exec(line.trim());
  if (!m) return null;
  return {
    speaker: CANON[m[1]!]!,
    label: m[1]!,
    ...(m[2] ? { no: Number(fromSubscript(m[2])), ...(/[₀-₉]/.test(m[2]) ? { noText: m[2] } : {}) } : {}),
    uncertain: !!m[3],
    text: m[4] ?? '',
  };
}

/** 축어록 줄 번호: 상1, 내1, 상2 … (화자별 순번). 화자 불확실 줄은 번호 없이 '?' */
export function numberVerbatim(lines: { role: string; text: string; uncertain: boolean }[]): string[] {
  let c = 0;
  let k = 0;
  return lines.map((l) => {
    if (l.uncertain || (l.role !== 'counselor' && l.role !== 'client')) return `?(확인 필요): ${l.text}`;
    const n = l.role === 'counselor' ? ++c : ++k;
    return `${l.role === 'counselor' ? '상' : '내'}${n}: ${l.text}`;
  });
}

/** 맞장구·추임새만 있는 말 (음, 네 네, 그렇죠 …) */
const FILLER = /^(음+|어+|아+|네+|예+|응+|그렇죠|그쵸|맞아요|네\s*네|예\s*예|음\s*음)[\s.!?…~,]*$/;

/**
 * 사례보고서 축어록 (정돈): 보고서용이라 주고받는 대화로 읽히게 한다. 말한 내용은 바꾸지 않는다.
 * - 화자 판정이 '추정'이어도 그 화자로 쓴다 (불명으로 남기지 않음).
 * - 화자를 전혀 모르는 줄: 맞장구·추임새면 빼고, 아니면 주고받기 흐름대로 앞사람의 상대 화자로 둔다.
 * - 같은 사람이 이어 말한 줄은 한 줄로 합친다. 번호는 화자별 순번 (상1, 내1, 상2 …).
 * 문장 다듬기(전사 오류·조각난 말)는 외부 AI로 쓸 때 한다.
 */
export function numberVerbatimTidy(
  lines: { role: string; text: string; uncertain: boolean; backchannel?: boolean }[],
): string[] {
  const turns: { role: 'counselor' | 'client'; text: string }[] = [];
  for (const l of lines) {
    const text = l.text.trim();
    if (!text) continue;
    const known = l.role === 'counselor' || l.role === 'client' ? l.role : null;
    if (!known && (l.backchannel || FILLER.test(text))) continue;
    if (known && l.backchannel && FILLER.test(text)) continue;
    const last = turns.at(-1);
    const role = known ?? (last ? (last.role === 'counselor' ? 'client' : 'counselor') : 'counselor');
    if (last?.role === role) last.text = `${last.text} ${text}`;
    else turns.push({ role, text });
  }
  let c = 0;
  let k = 0;
  return turns.map((t) => (t.role === 'counselor' ? `상${++c}: ${t.text}` : `내${++k}: ${t.text}`));
}

/** "| 가 | 나 |" 표 줄 → 칸 목록. 표 줄이 아니면 null */
export function parseTableRow(line: string): string[] | null {
  const t = line.trim();
  if (!t.startsWith('|') || !t.endsWith('|') || t.length < 3) return null;
  return t
    .slice(1, -1)
    .split('|')
    .map((c) => c.trim());
}

/** 칸 내용을 표 덩어리와 일반 줄로 나눈다 */
export function splitTables(
  lines: string[],
): ({ kind: 'lines'; lines: string[] } | { kind: 'table'; rows: string[][] })[] {
  const out: ({ kind: 'lines'; lines: string[] } | { kind: 'table'; rows: string[][] })[] = [];
  for (const l of lines) {
    const row = parseTableRow(l);
    const last = out.at(-1);
    if (row) {
      if (last?.kind === 'table') last.rows.push(row);
      else out.push({ kind: 'table', rows: [row] });
    } else if (last?.kind === 'lines') last.lines.push(l);
    else out.push({ kind: 'lines', lines: [l] });
  }
  return out;
}

/**
 * 축어록 칸(isVerbatimLabel) 안의 상N/내N 줄과, 제목에 들어 있는 범례만 고른 표기로 바꾼다.
 * 다른 칸의 문장은 건드리지 않는다.
 */
export function applyNotation(content: string, notation: VerbatimNotation): string {
  if (notation === 'ko') return content;
  const n = VERBATIM_NOTATIONS[notation];
  let inVerbatim = false;
  return content
    .split('\n')
    .map((line) => {
      const h = /^(#{1,4})\s+(.*)$/.exec(line);
      if (h) {
        inVerbatim = h[1]!.length >= 3 && isVerbatimLabel(h[2]!);
        return line.replace(VERBATIM_NOTATIONS.ko.legend, n.legend);
      }
      if (!inVerbatim) return line;
      const m = /^(\s*)(상|내)(\d+)?((?:\(확인 필요\))?\s*:)/.exec(line);
      if (!m) return line;
      const no = notation === 'pcsub' ? toSubscript(m[3] ?? '') : (m[3] ?? '');
      return `${m[1]}${m[2] === '상' ? n.counselor : n.client}${no}${m[4]}${line.slice(m[0].length)}`;
    })
    .join('\n');
}

/**
 * 대화록 번호 (목회상담 대화록 양식): 상담자 발언마다 번호가 하나 오르고, 이어지는 내담자 발언은 같은 번호 (P1 → C1, P2 → C2).
 * 같은 사람이 연달아 말한 줄은 한 문단으로 합친다. 화자 불확실 줄은 합치지 않고 번호 없이 둔다.
 */
export function numberDialogue(
  lines: { role: string; text: string; uncertain: boolean; backchannel?: boolean }[],
): string[] {
  // 문장 조각이 아니라 말한 차례(턴) 단위로: 같은 사람이 이어 말한 문장은 한 문단으로 합친다.
  // 상담자/내담자로 판정된 줄은 '추정'이어도 그 화자로 쓴다 (문서 초안 — 틀리면 전사에서 화자를 고치고 다시 만든다).
  // 화자를 전혀 모르는 줄만 '?'로 두고, 모르는 맞장구('네', '음')는 뺀다
  const turns: { role: 'counselor' | 'client' | null; text: string }[] = [];
  for (const l of lines) {
    const role = l.role === 'counselor' || l.role === 'client' ? l.role : null;
    const text = l.text.trim();
    if (!text || (!role && l.backchannel)) continue;
    const last = turns.at(-1);
    if (last && last.role === role) last.text = `${last.text} ${text}`;
    else turns.push({ role, text });
  }
  let n = 0;
  return turns.map((t) => {
    if (!t.role) return `?: ${t.text}`;
    if (t.role === 'counselor') return `상${++n}: ${t.text}`;
    return `내${Math.max(n, 1)}: ${t.text}`;
  });
}

/**
 * 표 열 너비(%): 글이 많은 열은 넓게, 번호·날짜 같은 짧은 열은 좁게.
 * 열마다 가장 긴 칸의 글자 수로 정하되 너무 좁거나 넓지 않게 (최소 8%).
 */
export function columnWidths(rows: string[][]): number[] {
  const cols = Math.max(0, ...rows.map((r) => r.length));
  if (!cols) return [];
  const full = rows.filter((r) => r.length === cols);
  const len = Array.from({ length: cols }, (_, j) =>
    Math.max(2, ...(full.length ? full : rows).map((r) => (r[j] ?? '').trim().length)),
  );
  // 번호 같은 짧은 열은 좁게: 글자 수에 덜 민감한 가중치
  const weight = len.map((n) => Math.min(Math.pow(n, 0.8), 60));
  const total = weight.reduce((a, b) => a + b, 0);
  let w = weight.map((x) => Math.max(6, (x / total) * 100));
  const sum = w.reduce((a, b) => a + b, 0);
  w = w.map((x) => (x / sum) * 100);
  return w.map((x) => Math.round(x * 10) / 10);
}

/**
 * 표의 머리 칸(굵게·회색): 칸이 모자란 줄의 앞 칸, 첫 줄이 짧은 표의 둘째 줄,
 * 그리고 첫 줄이 짧은 이름들로만 된 표(예: "번호 | 문항 | 응답")의 첫 줄.
 */
export function isHeaderCell(rows: string[][], i: number, j: number): boolean {
  const cols = Math.max(...rows.map((r) => r.length));
  const r = rows[i]!;
  if (r.length < cols && j < r.length - 1) return true;
  if (i === 1 && rows.length > 1 && r.length === cols && rows[0]!.length < cols) return true;
  return (
    i === 0 &&
    rows.length > 1 &&
    cols > 1 &&
    r.length === cols &&
    r.every((c) => c.trim().length > 0 && c.trim().length <= 12)
  );
}

/**
 * 대화록(작성표 양식): 여섯 구성 요소의 본문 칸은 큰 제목(I.~VI.) 아래 바로 본문만 보이게 한다 (칸 소제목은 앱 편집용).
 * 덧붙인 칸(사용한 기법, 수퍼비전 질문)은 제목을 그대로 보인다.
 */
export const isDialogueMainField = (label: string) =>
  /^(사실 파악과 준비|관찰|대화 내용|분석|신학적 의미|앞으로의 계획)(\s|\(|$)/.test(label.trim());
/** 대화록 III의 '(녹음 … 구간 …)' 안내 줄: 앱에서만 쓰고 보기·인쇄·Word에는 넣지 않는다 */
export const isDialogueRangeNote = (text: string) => text.trim().startsWith('(녹음 ');

/** 예전 대화록에 들어갔던 작성표 밖의 칸 (기법 정리·수퍼비전 질문): 보기·인쇄·Word에서 빼고 작성표 항목만 보인다 */
export const isDialogueExtraField = (label: string) =>
  /^(사용한 상담기법과 이론|수퍼비전에서 다룰 질문)/.test(label.trim());
